import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DatabaseSeedPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const token = localStorage.getItem('adminToken');

  const checkStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/api/admin/seed/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to check status');
    } finally {
      setLoading(false);
    }
  };

  const seedDatabase = async () => {
    if (!window.confirm('This will import all 82 scenarios into the database. Continue?')) {
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      // First, export scenarios from preview (if needed)
      // Then import the exported scenarios
      const response = await axios.post(
        `${API_URL}/api/admin/seed/scenarios`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setResult(response.data);
      
      // Refresh status
      setTimeout(checkStatus, 1000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to seed database');
    } finally {
      setLoading(false);
    }
  };

  const importFromFile = async (file) => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      const response = await axios.post(
        `${API_URL}/api/admin/seed/import-scenarios`,
        data.scenarios || data,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      
      setResult(response.data);
      setTimeout(checkStatus, 1000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to import scenarios');
    } finally {
      setLoading(false);
    }
  };

  const exportScenarios = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/seed/export-scenarios`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Download as JSON file
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `scenarios_export_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to export scenarios');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (token) {
      checkStatus();
    }
  }, []);

  if (!token) {
    return (
      <div className="container mx-auto p-6">
        <Alert>
          <AlertDescription>
            Please log in as admin to access database seeding tools.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Database Management</h1>
        <p className="text-gray-600 mt-2">Seed and manage production database</p>
      </div>

      {/* Current Status */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Current Database Status</CardTitle>
          <CardDescription>Overview of your database contents</CardDescription>
        </CardHeader>
        <CardContent>
          {status ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Database</p>
                  <p className="text-lg font-semibold">{status.database}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Status</p>
                  <p className="text-lg font-semibold capitalize">{status.status}</p>
                </div>
              </div>

              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">Current Counts:</p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-blue-50 p-3 rounded">
                    <p className="text-xs text-gray-600">Total Scenarios</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {status.current_counts.total_scenarios}
                    </p>
                  </div>
                  <div className="bg-green-50 p-3 rounded">
                    <p className="text-xs text-gray-600">Full Scenarios</p>
                    <p className="text-2xl font-bold text-green-600">
                      {status.current_counts.full_scenarios}
                    </p>
                  </div>
                  <div className="bg-purple-50 p-3 rounded">
                    <p className="text-xs text-gray-600">Micro Scenarios</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {status.current_counts.micro_scenarios}
                    </p>
                  </div>
                </div>
              </div>

              {status.needs_seeding && (
                <Alert className="bg-yellow-50 border-yellow-200">
                  <AlertDescription className="text-yellow-800">
                    ⚠️ Database needs seeding. Expected: {status.expected_counts.total_scenarios} scenarios,
                    Current: {status.current_counts.total_scenarios}
                  </AlertDescription>
                </Alert>
              )}

              {!status.needs_seeding && (
                <Alert className="bg-green-50 border-green-200">
                  <AlertDescription className="text-green-800">
                    ✅ Database is fully seeded with all {status.expected_counts.total_scenarios} scenarios!
                  </AlertDescription>
                </Alert>
              )}
            </div>
          ) : (
            <div className="text-center py-4">
              <Button onClick={checkStatus} disabled={loading}>
                {loading ? 'Checking...' : 'Check Status'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Actions */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Database Actions</CardTitle>
          <CardDescription>Seed or export database content</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Method 1: Direct Seed (if on same environment) */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Option 1: Import Scenarios (Recommended)</h3>
            <p className="text-sm text-gray-600 mb-3">
              Upload the scenarios JSON file exported from preview environment
            </p>
            <input
              type="file"
              accept=".json"
              onChange={(e) => e.target.files[0] && importFromFile(e.target.files[0])}
              disabled={loading}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
          </div>

          <div className="border-t pt-4">
            <h3 className="font-semibold text-gray-900 mb-2">Option 2: Export Current Scenarios</h3>
            <p className="text-sm text-gray-600 mb-3">
              Download all scenarios from this database as JSON
            </p>
            <Button onClick={exportScenarios} disabled={loading} variant="outline">
              {loading ? 'Exporting...' : 'Export Scenarios'}
            </Button>
          </div>

          <div className="border-t pt-4">
            <Button 
              onClick={checkStatus} 
              disabled={loading}
              variant="ghost"
              className="w-full"
            >
              🔄 Refresh Status
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {result && (
        <Alert className="mb-6 bg-green-50 border-green-200">
          <AlertDescription>
            <div className="text-green-800">
              <p className="font-semibold mb-2">✅ {result.message}</p>
              {result.results && (
                <div className="text-sm space-y-1">
                  {result.results.added > 0 && <p>Added: {result.results.added}</p>}
                  {result.results.updated > 0 && <p>Updated: {result.results.updated}</p>}
                  {result.added && <p>Added: {result.added}</p>}
                  {result.updated && <p>Updated: {result.updated}</p>}
                  {result.total && <p>Total: {result.total}</p>}
                </div>
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert className="mb-6 bg-red-50 border-red-200">
          <AlertDescription className="text-red-800">
            ❌ {error}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
